import cx from 'classnames';
import { Icon, AnyIcon } from '../icon/Icon';
import { forwardRef } from 'react';
import typography from '~styles/typography.scss';
import styles from './Button.scss';

export enum ButtonVariants {
  Default = 'default',
  DefaultWhite = 'defaultWhite',
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'outline',
  Error = 'error',
}

type ButtonType = 'reset' | 'submit' | 'button' | undefined;

interface WithTypeFormProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type: 'submit' | 'reset';
}

interface WithTypeButtonClickProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  type: 'button';
}

interface WithNoTypeProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  type?: never;
}

type ButtonInteractionProps =
  | WithTypeFormProps
  | WithTypeButtonClickProps
  | WithNoTypeProps;

type ButtonContentProps =
  | { children: React.ReactNode; label?: never }
  | { label: string; children?: never };

export type ButtonProps = {
  className?: string;
  dataTestId?: string;
  form?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  name?: string;
  variant: ButtonVariants;
  iconName?: AnyIcon;
  iconPosition?: 'left' | 'right';
} & ButtonInteractionProps &
  ButtonContentProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      dataTestId,
      isDisabled = false,
      isLoading = false,
      label,
      children,
      name,
      onClick,
      variant,
      iconName,
      iconPosition = 'left',
      ...props
    },
    ref,
  ) => {
    const buttonClassNames = cx({
      [styles.btnDefault]: variant === ButtonVariants.Default,
      [styles.btnDefaultWhite]: variant === ButtonVariants.DefaultWhite,
      [styles.btnTertiary]: variant === ButtonVariants.Tertiary,
      [styles.btnPrimary]: variant === ButtonVariants.Primary,
      [styles.btnSecondary]: variant === ButtonVariants.Secondary,
      [styles.btnError]: variant === ButtonVariants.Error,
      [styles.btn]: true,
      [typography.h6]: true,
    });

    let buttonType: ButtonType = 'button';
    if ('type' in props) {
      buttonType = props.type;
    }

    return (
      <button
        className={cx(buttonClassNames, className)}
        data-testid={dataTestId}
        disabled={isDisabled || isLoading || false}
        name={name}
        onClick={onClick}
        ref={ref}
        type={buttonType}
      >
        {iconName && iconPosition === 'left' && <Icon name={iconName} />}
        {children || label}
        {iconName && iconPosition === 'right' && <Icon name={iconName} />}
      </button>
    );
  },
);
Button.displayName = 'Button';
